/**
 * Onboarding — first-run illustrated guide. Names the player, then walks through
 * the core loop with a small SVG diagram per step. Bilingual (EN/TH) via i18n;
 * a language toggle sits in the top corner. Shown only when no save exists.
 */
import { useState, type ReactNode } from "react";
import { t, useLang, toggleLang } from "@/i18n";

interface Props {
  onStart: (name: string) => void;
}

interface Step {
  title: string;
  body: string;
  art: ReactNode;
}

// --- little SVG illustrations (game palette) ---
const Ship = (x: number, y: number, s = 1): ReactNode => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <path d="M0,-14 L9,10 L0,4 L-9,10 Z" fill="#cfe0f5" stroke="#7fb0ff" strokeWidth="1.2" />
    <circle cx="0" cy="0" r="3" fill="#9fe6ff" />
  </g>
);

const artMine: ReactNode = (
  <svg viewBox="0 0 240 130" className="onb-art">
    <circle cx="180" cy="60" r="30" fill="#3a4a3a" stroke="#5fe08a" strokeWidth="2" />
    <circle cx="172" cy="52" r="5" fill="#2c3a2c" />
    <circle cx="190" cy="70" r="7" fill="#2c3a2c" />
    <line x1="70" y1="60" x2="150" y2="60" stroke="#9fe6ff" strokeWidth="3" strokeDasharray="2 4" />
    <circle cx="150" cy="60" r="6" fill="#eaffff" opacity="0.8" />
    {Ship(58, 60, 1.5)}
  </svg>
);

const artGrow: ReactNode = (
  <svg viewBox="0 0 240 130" className="onb-art">
    {[0, 1, 2].map((i) => (
      <g key={i}>
        <line
          x1={60 + i * 60}
          y1="65"
          x2={120 + i * 60}
          y2="65"
          stroke="#39c6ff"
          strokeWidth="2"
          opacity={i < 2 ? 1 : 0.4}
        />
      </g>
    ))}
    {[0, 1, 2, 3].map((i) => (
      <g key={i}>
        <rect
          x={40 + i * 60}
          y="48"
          width="34"
          height="34"
          rx="7"
          fill={i < 2 ? "#1b6ef3" : "rgba(255,255,255,0.08)"}
          stroke={i < 2 ? "#7fb0ff" : "#39c6ff"}
          strokeWidth="1.5"
        />
        <text x={57 + i * 60} y="70" textAnchor="middle" fontSize="16" fill="#fff">
          {i < 2 ? "✓" : "+"}
        </text>
      </g>
    ))}
  </svg>
);

const artPlanets: ReactNode = (
  <svg viewBox="0 0 240 130" className="onb-art">
    <circle cx="45" cy="65" r="18" fill="#3d7bff" />
    <text x="45" y="34" textAnchor="middle" fontSize="16">🔧</text>
    <circle cx="120" cy="65" r="18" fill="#b98cff" />
    <text x="120" y="34" textAnchor="middle" fontSize="16">🛒</text>
    <circle cx="195" cy="65" r="18" fill="#ffb347" />
    <text x="195" y="34" textAnchor="middle" fontSize="16">🍺</text>
    <circle cx="120" cy="65" r="52" fill="none" stroke="#2a3a52" strokeWidth="1" strokeDasharray="3 3" />
  </svg>
);

const artDefend: ReactNode = (
  <svg viewBox="0 0 240 130" className="onb-art">
    <circle cx="70" cy="65" r="24" fill="none" stroke="#39c6ff" strokeWidth="2" opacity="0.6" />
    {Ship(70, 65, 1.6)}
    <line x1="150" y1="55" x2="95" y2="60" stroke="#ff3a2a" strokeWidth="3" />
    <line x1="150" y1="75" x2="95" y2="70" stroke="#ff3a2a" strokeWidth="3" />
    <g transform="translate(175 65)">
      <path d="M0,-12 L8,9 L0,3 L-8,9 Z" fill="#3a2230" stroke="#ff5a4a" strokeWidth="1.4" />
    </g>
  </svg>
);

const artMap: ReactNode = (
  <svg viewBox="0 0 240 130" className="onb-art">
    <rect x="70" y="20" width="100" height="90" rx="8" fill="rgba(6,10,18,0.85)" stroke="#3a4a63" />
    <circle cx="120" cy="65" r="9" fill="#3d7bff" />
    <circle cx="95" cy="42" r="3.5" fill="#5fe08a" />
    <circle cx="150" cy="50" r="3.5" fill="#5fe08a" />
    <circle cx="140" cy="88" r="3" fill="#b98cff" />
    <circle cx="100" cy="90" r="3.5" fill="#ff4a4a" />
    <circle cx="120" cy="65" r="3.5" fill="#ffe14a" />
  </svg>
);

const artStart: ReactNode = (
  <svg viewBox="0 0 240 130" className="onb-art">
    <path d="M60,100 Q120,10 190,45" fill="none" stroke="#7b5cff" strokeWidth="2" strokeDasharray="4 5" />
    {Ship(190, 45, 1.8)}
    <text x="52" y="112" fontSize="22">🏁</text>
  </svg>
);

export default function Onboarding({ onStart }: Props) {
  const lang = useLang();
  const [page, setPage] = useState(-1); // -1 = name entry
  const [name, setName] = useState("");
  const trimmed = name.trim();

  const STEPS: Step[] = [
    {
      title: t("Mine the belt"),
      body: t(
        "You command a mining Carrier. Tap an asteroid to fly over and mine it with your hull lasers — richer rocks give more minerals and XP. Tap empty space to move.",
      ),
      art: artMine,
    },
    {
      title: t("Grow your ship"),
      body: t(
        "Spend minerals in the Upgrades tree to boost mining, range and speed. Refit to Mk II at a mechanical yard to unlock Drones, then push on to Mk III and the Mk IV Carrier.",
      ),
      art: artGrow,
    },
    {
      title: t("Planets & services"),
      body: t(
        "Leave a planet to fly the solar system. 🛒 Trade hubs swap minerals for Units; 🔧 Mechanical yards repair, refit and manage your Arsenal; 🍺 Space Taverns recruit crew. These planets are SAFE ZONES.",
      ),
      art: artPlanets,
    },
    {
      title: t("Defend yourself"),
      body: t(
        "Hostile Fighters make fast strafing runs, firing red bolts. Refit to Mk II to mount turrets that auto-target them. Mining pauses during combat — watch your ⛉ shield and ❤ hull.",
      ),
      art: artDefend,
    },
    {
      title: t("Navigate & warp"),
      body: t(
        "The view stays zoomed out, so use the top-right minimap: green dots are asteroids, purple moons, blue the planet, red enemies — yellow is you. Craft Warp Fuel to jump to a richer system.",
      ),
      art: artMap,
    },
    {
      title: t("Where to start"),
      body: t(
        "Head to the nearest asteroids and mine until you can afford your first upgrades. Build toward the Mk II refit at a 🔧 mechanical planet to unlock drones and turrets. Good luck, Captain!",
      ),
      art: artStart,
    },
  ];

  const LangToggle = (
    <div className="onb-lang">
      <button className={lang === "en" ? "on" : ""} onClick={() => toggleLang()}>
        EN
      </button>
      <button className={lang === "th" ? "on" : ""} onClick={() => toggleLang()}>
        ไทย
      </button>
    </div>
  );

  if (page === -1) {
    return (
      <div className="onb-backdrop">
        <div className="onb-panel">
          {LangToggle}
          <div className="onb-logo">🚀 {t("Space Adventura")}</div>
          <p className="onb-lead">
            {t(
              "A slow-burn space mining adventure. First — what should we call you, Captain?",
            )}
          </p>
          <input
            className="onb-input"
            value={name}
            maxLength={24}
            placeholder={t("Enter your name")}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && trimmed) setPage(0);
            }}
            autoFocus
          />
          <button
            className="hud-btn accent onb-next"
            disabled={!trimmed}
            onClick={() => setPage(0)}
          >
            {t("Begin")} ▸
          </button>
        </div>
      </div>
    );
  }

  const step = STEPS[page]!;
  const last = page === STEPS.length - 1;
  return (
    <div className="onb-backdrop">
      <div className="onb-panel">
        {LangToggle}
        <div className="onb-art-wrap">{step.art}</div>
        <div className="onb-step">
          {page + 1} / {STEPS.length}
        </div>
        <div className="onb-title">{step.title}</div>
        <p className="onb-body">{step.body}</p>
        <div className="onb-dots">
          {STEPS.map((_, i) => (
            <span key={i} className={`onb-dot${i === page ? " on" : ""}`} />
          ))}
        </div>
        <div className="onb-actions">
          <button
            className="hud-btn ghost"
            onClick={() => setPage((n) => Math.max(0, n - 1))}
            disabled={page === 0}
          >
            ◂ {t("Back")}
          </button>
          {last ? (
            <button className="hud-btn accent" onClick={() => onStart(trimmed)}>
              {t("Launch")} ▸
            </button>
          ) : (
            <button className="hud-btn accent" onClick={() => setPage((n) => n + 1)}>
              {t("Next")} ▸
            </button>
          )}
        </div>
        <button className="onb-skip" onClick={() => onStart(trimmed)}>
          {t("Skip guide")}
        </button>
      </div>
    </div>
  );
}
